import axios from 'axios';
import { useAuthStore } from '../stores/authStore';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5501/api';

export interface LoginData {
  email: string;
  password: string;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    organizationId?: string;
  };
}

export interface MenuItem {
  key: string;
  label: string;
  icon?: string;
  path?: string;
  permissions?: string[];
  requireAllPermissions?: boolean;
  children?: MenuItem[];
  divider?: boolean;
  type?: 'group' | 'item' | 'divider';
}

export interface MeResponse {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  organizationId?: string;
  orgRole?: string; // Organization-specific role (e.g., 'admin', 'member')
  roles: string[];
  permissions: string[];
  menu: MenuItem[];
  sessionId?: string | null;
}

export interface ClientSessionRow {
  id: string;
  userAgent: string | null;
  ipAddress: string | null;
  createdAt: string;
  lastActiveAt: string;
  revoked: boolean;
  isCurrent: boolean;
}

/**
 * Separate axios instance for auth endpoints.
 * This is intentionally kept separate from the main API instance to avoid
 * circular dependencies during token refresh (can't use interceptors that refresh tokens
 * on the endpoint that's doing the refresh).
 */
const authApi = axios.create({
  baseURL: `${API_URL}/auth`,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const authService = {
  async login(data: LoginData): Promise<AuthResponse> {
    const response = await authApi.post<AuthResponse>('/login', data);
    return response.data;
  },

  async refresh(refreshToken: string): Promise<AuthResponse> {
    const response = await authApi.post<AuthResponse>('/refresh', { refreshToken });
    return response.data;
  },

  async logout(accessToken: string): Promise<void> {
    await authApi.post(
      '/logout',
      {},
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
  },

  async getMe(accessToken: string): Promise<MeResponse> {
    const response = await authApi.get<MeResponse>('/me', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  async listSessions(): Promise<ClientSessionRow[]> {
    const token = useAuthStore.getState().accessToken;
    if (!token) throw new Error('Not authenticated');
    const response = await authApi.get<ClientSessionRow[]>('/sessions', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async revokeSession(sessionId: string): Promise<void> {
    const token = useAuthStore.getState().accessToken;
    if (!token) throw new Error('Not authenticated');
    await authApi.delete(`/sessions/${sessionId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
};
